import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, SimpleGrid, Card, CardHeader, CardBody, CardFooter, Text, Flex, Icon, useToast, Button, useMediaQuery, Stack, VStack
} from '@chakra-ui/react';
import { FaStar, FaRegStar, FaStarHalfAlt } from 'react-icons/fa';
import { useLanguage, useTranslations } from '../LanguageContext';
import { Link as RouterLink } from 'react-router-dom';
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { storage } from '../firebaseConfig';
import ImageCarousel from '../components/ImageCarousel';

const localeMap = {
  en: 'en-US',
  de: 'de-DE',
} as const;

interface Review {
  _id: string;
  Name: string;
  carName: string;
  review: string;
  rating: number;
  ownerComment?: string;
  createdAt: string;
  approved: boolean;
}

interface ImageData {
  url: string;
  approved: boolean;
}


const ReviewCards: React.FC = () => {
  const { language } = useLanguage();
  const translations = useTranslations();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [images, setImages] = useState<string[]>([]);
  const toast = useToast();
  const reviewsPerPage = 8;
  const reviewsPerPageMobile = 3;

  useEffect(() => {
    fetchReviews();
    fetchImages();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get<Review[]>('https://backend.afmobile.de/api/reviews');
      const sortedReviews = response.data.sort((a: Review, b: Review) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setReviews(sortedReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };
  const fetchImages = async () => {
    try {
      // Fetch only approved images from backend
      const { data: approvedImages }: { data: ImageData[] } = await axios.get('https://backend.afmobile.de/api/images/approved');
      
      console.log('Approved images from backend:', approvedImages);
      
      // Extract URLs from the approved images
      const approvedImageUrls = approvedImages.map((image: ImageData) => image.url);
      
      console.log('Approved image URLs:', approvedImageUrls);
      
      // Fetch download URLs from Firebase Storage
      const imageRefs = ref(storage, 'uploads');
      const imageList = await listAll(imageRefs);
      
      console.log('Images in Firebase Storage:', imageList.items);
      
      // Get download URLs for items in Firebase Storage
      const imageUrls = await Promise.all(
        imageList.items.map(async item => {
          try {
            const url = await getDownloadURL(item);
            console.log('Fetched download URL:', url);
            return url;
          } catch (err) {
            console.error('Error fetching download URL:', err);
            return null;
          }
        })
      );
      
      // Filter out any null values from failed URL fetches
      const validImageUrls = imageUrls.filter(url => url !== null) as string[];
      
      console.log('Final image URLs:', validImageUrls);
      
      // Only set the images that are in the list of approved URLs
      const filteredImageUrls = validImageUrls.filter(url => approvedImageUrls.includes(url));
      
      console.log('Filtered image URLs:', filteredImageUrls);
      
      setImages(filteredImageUrls);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };
  
  
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (bottom) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

    return (
      <Flex>
        {[...Array(fullStars)].map((_, index) => (
          <Icon as={FaStar} key={index} color="yellow.400" />
        ))}
        {halfStar && <Icon as={FaStarHalfAlt} color="yellow.400" />}
        {[...Array(emptyStars)].map((_, index) => (
          <Icon as={FaRegStar} key={index} color="yellow.400" />
        ))}
      </Flex>
    );
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const totalRating = reviews.reduce((acc, review) => acc + review.rating, 0);
    return totalRating / reviews.length;
  };

  const averageRating = calculateAverageRating();

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const formatDate = (date: string | Date) => {
    const locale = localeMap[language] ?? 'en-US';
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const approvedReviews = reviews.filter(review => review.approved);

  const currentReviews = isMobile
    ? approvedReviews.slice(0, (currentPage + 1) * reviewsPerPageMobile)
    : approvedReviews.slice(currentPage * reviewsPerPage, (currentPage + 1) * reviewsPerPage);

  return (
    <Box p={4} onScroll={isMobile ? handleScroll : undefined} height={isMobile ? '80vh' : 'auto'} overflowY={isMobile ? 'scroll' : 'visible'}>
      <VStack spacing={4} mb={8}>
        <ImageCarousel images={images} />
        <Box textAlign="center">
      <Text fontSize="2xl" fontWeight="bold">
        {translations.averageRating}: {averageRating.toFixed(1)}
      </Text>
    </Box>
        {renderStars(averageRating)}
        <Button as={RouterLink} to="/review" colorScheme="teal" variant="solid">
          {translations.submitReviewButton}
        </Button>
      </VStack>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
        {currentReviews.map((review) => (
          <Card key={review._id} minH="300px" width={isMobile ? "100%" : "auto"}>
            <CardHeader>
              <Flex justifyContent="space-between">
                <Box textAlign="left">
                  <Text fontWeight="bold">{translations.name}: {review.Name}</Text>
                  <Text>{translations.carName}: {review.carName}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {translations.dateAdded}: {formatDate(review.createdAt)}
                  </Text>
                </Box>
              </Flex>
            </CardHeader>
            <CardBody textAlign="left">
              <Text mb={2}>{translations.review}: {review.review}</Text>
              {renderStars(review.rating)}
            </CardBody>
            {review.ownerComment && (
              <CardFooter textAlign="left">
                <Text>{translations.ownerComment}: {review.ownerComment}</Text>
              </CardFooter>
            )}
          </Card>
        ))}
      </SimpleGrid>

      {!isMobile && (
        <Flex justifyContent="center" mt={4}>
          <Stack direction="row" spacing={4}>
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 0}
            >
              {translations.previous}
            </Button>
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={(currentPage + 1) * reviewsPerPage >= approvedReviews.length}
            >
              {translations.next}
            </Button>
          </Stack>
        </Flex>
      )}
    </Box>
  );
};

export default ReviewCards;
